import { NavLink, useLocation } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useState, type ReactNode } from 'react'
import { api, type MeResponse } from '../api/client'
import { useAgentSettings } from '../api/visitorsAgent'
import { useRepliesEnabled } from '../lib/useRepliesEnabled'
import { useCampaignDeskEnabled } from '../lib/useCampaignDeskEnabled'
import { editorDeskLinks } from '../lib/deskAccess'
import { endsGroup, startsGroup } from '../lib/navGroups'
import { getQueue } from '../api/tracks'
import { queueCountSource } from '../editorial/trackFinder'
import { badgeText, supportNavChildren, type DeskSummary } from '../lib/supportDesk'

type Item = {
  to: string
  code: string
  label: string
  icon: ReactNode
  badge?: 'duplicates' | 'lab' | 'contact' | 'feedback' | 'unthreaded' | 'email'
  children?: Array<{ to: string; label: string; end?: boolean; /** Active on any path under this prefix. */ match?: string }>
  /** Items sharing a group render together under that group's heading. */
  group?: 'support'
}

// Stands in for the whole support group when the desk is on (no `group`, so it
// gets no heading of its own).
const SUPPORT_DESK_ITEM: Item = { to: '/support', code: '07c', label: 'Customer Support', icon: <IconMail /> }

const GROUP_LABELS: Record<NonNullable<Item['group']>, string> = { support: 'Customer Support' }

const NAV: Item[] = [
  { to: '/', code: '01', label: 'Overview', icon: <IconGauge /> },
  {
    to: '/subscribers',
    code: '02',
    label: 'Subscribers',
    icon: <IconUsers />,
    children: [
      { to: '/subscribers/test-pools', label: 'Test Pools' },
    ],
  },
  // Only what we sold ourselves. SimpleCirc-sourced orders reach the
  // console through the subscriber record and the daily tie-out instead.
  { to: '/orders', code: '02b', label: 'Orders', icon: <IconReceipt /> },
  // Money that arrives in the mail. Next to Orders because that is where
  // the check lands once it is keyed, and it is driven every morning.
  { to: '/offline-intake', code: '02c', label: 'Record a Check', icon: <IconReceipt /> },
  // Memberships we give away. Beside the check screen because it is the
  // other way a membership starts without a card, and it uses the same
  // duplicate warning.
  { to: '/comps/new', code: '02d', label: 'Comp a Member', icon: <IconUsers /> },
  // Printed copies to fulfil. Beside the other order screens because that is
  // what it is, and because it is the ONLY place a stranded or failed print
  // charge surfaces — print has no email and nothing alerts on it.
  { to: '/print-orders', code: '02e', label: 'Print', icon: <IconReceipt /> },
  // Our engine's renewals, for titles cut over from SimpleCirc. Beside the
  // AR Report, which is SimpleCirc's side of the same question.
  { to: '/renewals', code: '02f', label: 'Renewals', icon: <IconAuditCheck /> },
  { to: '/ar-report', code: '03', label: 'AR Report', icon: <IconAuditCheck /> },
  {
    to: '/segments',
    code: '04',
    label: 'Segments',
    icon: <IconBranching />,
    children: [
      { to: '/segments/with-email', label: 'With Email' },
      { to: '/segments/without-email', label: 'Without Email' },
    ],
  },
  { to: '/templates', code: '05', label: 'Templates', icon: <IconEnvelope /> },
  { to: '/campaigns', code: '06', label: 'Campaigns', icon: <IconBroadcast /> },
  // New-issue emails; admin + operator (operators have no other email screen).
  { to: '/issue-announcements', code: '06a', label: 'Issue announcements', icon: <IconBroadcast /> },
  { to: '/mailings', code: '06b', label: 'Mailings', icon: <IconMail /> },
  { to: '/newsletters', code: '06c', label: 'Newsletters', icon: <IconMail /> },
  { to: '/events', code: '07', label: 'Event Log', icon: <IconStream /> },
  { to: '/emails', code: '07b', label: 'Emails Log', icon: <IconEnvelope /> },
  // Customer Support: everything staff use to answer customers.
  // Inbound from the public contact form. Badged because nothing from
  // that form is emailed anywhere — if nobody opens this page, nobody
  // sees the message.
  { to: '/contact', code: '07c', label: 'Contact', icon: <IconMail />, badge: 'contact', group: 'support' },
  // Reader-site feedback sheet. Also emailed, but the email can be off or
  // refused, so the count lives here too.
  { to: '/feedback', code: '07d', label: 'Feedback', icon: <IconMail />, badge: 'feedback', group: 'support' },
  // Mail sent to service@airage.com, copied here by the mailbox.
  { to: '/email', code: '07g', label: 'Email', icon: <IconMail />, badge: 'email', group: 'support' },
  // Customer emails that reached a reply address we couldn't match.
  { to: '/unthreaded', code: '07f', label: 'Unthreaded', icon: <IconMail />, badge: 'unthreaded', group: 'support' },
  { to: '/known-issues', code: '07e', label: 'Known issues', icon: <IconMail />, group: 'support' },
  // How-to for the reply desk; static help for the pages above.
  { to: '/help/reply-desk', code: '07h', label: 'Reply desk guide', icon: <IconMail />, group: 'support' },
  { to: '/duplicates', code: '08', label: 'Duplicates', icon: <IconDuplicates />, badge: 'duplicates' },
  // Sync Health anchored at the bottom — pipeline telemetry (webhook
  // freshness, CSV reconciliation, validation coverage, sync-run log) in
  // one place, visually separated from the composition workflow above.
  { to: '/sync', code: '09', label: 'Sync Health', icon: <IconOrbit /> },
  { to: '/marketing-landing', code: '10', label: 'Marketing', icon: <IconMegaphone /> },
  { to: '/promo-codes', code: '10b', label: 'Promo Codes', icon: <IconMegaphone /> },
  { to: '/topic-pages', code: '11', label: 'Topic Pages', icon: <IconMegaphone /> },
  { to: '/sections', code: '11b', label: 'Sections', icon: <IconMegaphone /> },
  { to: '/agent-proposals', code: '12', label: 'Agent Queue', icon: <IconMegaphone /> },
  // Read-only: which external credentials this deployment has.
  { to: '/settings', code: '13', label: 'Settings', icon: <IconGauge /> },
  { to: '/users', code: '14', label: 'Users', icon: <IconGauge /> },
  { to: '/accounting', code: '15', label: 'Accounting', icon: <IconGauge /> },
]

// Lab nav items — appended below the main nav when LAB_ENABLED=true.
// Still-in-development section; visually separated by a divider + `LAB`
// badge so operators know these are experimental surfaces.
const LAB_NAV: Item[] = [
  { to: '/lab/archive', code: 'L1', label: 'Archive', icon: <IconArchive />, badge: 'lab' },
  { to: '/lab/content', code: 'L2', label: 'Content Selector', icon: <IconSelector />, badge: 'lab' },
  { to: '/lab/dry-run', code: 'L3', label: 'Campaign Dry-Run', icon: <IconDryRun />, badge: 'lab' },
  { to: '/lab/templates', code: 'L4', label: 'Lab Templates', icon: <IconLabTemplate />, badge: 'lab' },
  { to: '/lab/trailers', code: 'L5', label: 'Trailers', icon: <IconFilm />, badge: 'lab' },
  { to: '/lab/issues', code: 'L6', label: 'Issues', icon: <IconArchive />, badge: 'lab' },
]

export function NavRail() {
  // Light polling of the dashboard count is fine; stale 5m cache keeps cost down.
  // An editor account (Flight Line drafts only) sees one link and must not
  // fire the console's other queries, which would 403.
  const { data: me } = useQuery<MeResponse>({ queryKey: ['me'], queryFn: () => api.get<MeResponse>('/api/me'), staleTime: 60_000, retry: false })
  // A writer is an editor without publishing: the same one link.
  const editor = me?.role === 'editor' || me?.role === 'writer'
  // An operator account is Customer Support staff: the desk and its pages, nothing else of the console.
  const operator = me?.role === 'operator'
  const visitorsAgent = useAgentSettings(me?.role === 'admin')
  const navLoc = useLocation()
  const { data: dash } = useQuery<{
    totals?: { pending_duplicates?: number; stuck_issues?: number; tracks_queue_open?: number; tracks_queue_overdue?: number }
  }>({
    queryKey: ['navrail-dashboard-counts'],
    queryFn: () => api.get('/api/dashboard'),
    staleTime: 5 * 60 * 1000,
    enabled: !editor && !operator && !!me,
  })
  // Editors and writers with RC Car Action read the queue itself; a refusal is silent (no badge).
  const countSource = queueCountSource(me?.role, me?.sections)
  const { data: tq } = useQuery({
    queryKey: ['tracks', 'queue', false],
    queryFn: () => getQueue(false),
    staleTime: 5 * 60 * 1000,
    enabled: countSource === 'queue',
    retry: false,
  })
  const pendingDupes = dash?.totals?.pending_duplicates ?? 0
  const tracksOpen = (countSource === 'queue' ? tq?.open : dash?.totals?.tracks_queue_open) ?? 0
  const tracksOverdue = (countSource === 'queue' ? tq?.overdue : dash?.totals?.tracks_queue_overdue) ?? 0

  // The Customer Support desk. A 404 (flag off) or any error (a viewer or
  // operator refused) means "no desk": the menu stays as it always was.
  // Same key as the overview page, so the two share one request.
  const { data: deskSummary, isPending: deskPending } = useQuery<DeskSummary>({
    queryKey: ['support', 'summary'],
    queryFn: () => api.get<DeskSummary>('/api/support/summary'),
    staleTime: 60_000,
    // A 404/403 (desk off or not for this role) is not worth polling.
    refetchInterval: (q) => (q.state.status === 'error' ? false : 60_000),
    // A focus refetch would re-issue the flag-off 404 and hold back the legacy badges; the poll is enough.
    refetchOnWindowFocus: false,
    enabled: !editor && !!me,
    retry: false,
  })
  const deskOn = !!deskSummary
  // Wait for the summary to settle so a cold load neither fetches the old
  // counts nor flashes the old menu when the desk is on.
  const legacyBadges = !editor && !!me && !deskOn && !deskPending

  // Its own query rather than another field on /api/dashboard: the count
  // belongs to the inbox, and the inbox endpoint already returns it.
  const { data: contact } = useQuery<{ unread?: number }>({
    queryKey: ['navrail-contact-unread'],
    queryFn: () => api.get('/api/contact-messages?limit=1'),
    staleTime: 5 * 60 * 1000,
    enabled: legacyBadges && !operator,
  })
  const unreadContact = contact?.unread ?? 0
  const { data: feedback } = useQuery<{ unread?: number }>({
    queryKey: ['navrail-feedback-unread'],
    queryFn: () => api.get('/api/feedback-messages?limit=1'),
    staleTime: 5 * 60 * 1000,
    enabled: legacyBadges && !operator,
  })
  const unreadFeedback = feedback?.unread ?? 0
  const { data: emailInbox } = useQuery<{ unread?: number }>({
    queryKey: ['navrail-email-unread'],
    queryFn: () => api.get('/api/email-messages?limit=1'),
    staleTime: 5 * 60 * 1000,
    enabled: legacyBadges,
  })
  const unreadEmail = emailInbox?.unread ?? 0
  const { data: unthreaded } = useQuery<{ count_30d?: number }>({
    queryKey: ['navrail-unthreaded'],
    queryFn: () => api.get('/api/inbound/unthreaded?limit=1'),
    staleTime: 5 * 60 * 1000,
    // Desk on: the count still drives an "Unthreaded" child under Customer Support.
    enabled: legacyBadges || deskOn,
  })
  const unthreadedCount = unthreaded?.count_30d ?? 0
  const repliesEnabled = useRepliesEnabled()
  const campaignDesk = useCampaignDeskEnabled(me?.role === 'admin' || operator)
  const stuckIssues = dash?.totals?.stuck_issues ?? 0

  const { data: lab } = useQuery<{ enabled: boolean }>({
    queryKey: ['navrail-lab-enabled'],
    queryFn: () => api.get('/api/lab/enabled'),
    staleTime: 5 * 60 * 1000,
    enabled: !editor && !operator && !!me,
    retry: false,
  })

  return (
    <aside className="w-[212px] shrink-0 border-r border-ink-500 bg-ink-900/70 flex flex-col">
      <div className="px-5 py-5 border-b border-ink-500">
        <div className="font-serif text-[22px] leading-none tracking-tight text-paper">
          AAM<span className="text-amber-glow">Circ</span>
        </div>
        <div className="label mt-1">Subscription Ops Console</div>
      </div>

      <nav className="flex-1 py-4">
        {!editor && NAV.filter((item) => item.to !== '/known-issues' || repliesEnabled.data === true)
          .filter((item) => (item.to !== '/users' && item.to !== '/accounting') || me?.role === 'admin')
          .filter((item) => item.to !== '/issue-announcements' || me?.role === 'admin' || operator)
          // Operators see Customer Support (the old Contact/Feedback pages are admin's when the desk is off) and Issue announcements.
          .filter((item) => !operator || item.to === '/issue-announcements' || (item.group === 'support' && (deskOn || (item.to !== '/contact' && item.to !== '/feedback'))))
          // Desk on: the support group collapses into one expandable item.
          .filter((item) => !deskOn || item.group !== 'support' || item.to === '/contact')
          .map((item) => (deskOn && item.to === '/contact' ? SUPPORT_DESK_ITEM : item))
          .map((item, i, items) => (
          <div key={item.to}>
            {item.group && startsGroup(items, i) && (
              <div className="mt-5 mb-2 px-5 flex items-center gap-2">
                <span className="label">{GROUP_LABELS[item.group]}</span>
                <span className="h-px flex-1 bg-ink-500" />
              </div>
            )}
            {endsGroup(items, i) && <div className="mt-3" />}
          {item === SUPPORT_DESK_ITEM && deskSummary ? (
            <SupportMenu summary={deskSummary} repliesOn={repliesEnabled.data === true} unthreaded={unthreadedCount} />
          ) : (
          <NavItem
            item={item}
            pendingDupes={pendingDupes}
            unreadContact={unreadContact}
            unreadFeedback={unreadFeedback}
            unreadEmail={unreadEmail}
            unthreaded={unthreadedCount}
          />
          )}
          </div>
        ))}

        {(me?.role === 'admin' || operator) && (
          <NavLink to="/desk"
                   className={({ isActive }) => `group relative flex items-center gap-3 pl-5 pr-3 py-2.5 mt-3 transition-colors duration-150 ${isActive ? 'text-amber-glow' : 'text-paper-dim hover:text-paper'}`}>
            <span className="w-5 h-5 shrink-0"><IconUsers /></span>
            <span className="font-serif text-[14px] leading-none tracking-tight flex-1">Subscriber Desk</span>
          </NavLink>
        )}

        {me?.role === 'admin' && (
          <NavLink to={visitorsAgent.data != null ? '/visitors/desk/all/summary' : '/visitors/desk/all/today'}
                   className={() => `group relative flex items-center gap-3 pl-5 pr-3 py-2.5 transition-colors duration-150 ${navLoc.pathname.startsWith('/visitors/desk') ? 'text-amber-glow' : 'text-paper-dim hover:text-paper'}`}>
            <span className="w-5 h-5 shrink-0"><IconVisitors /></span>
            <span className="font-serif text-[14px] leading-none tracking-tight flex-1">Visitors Desk</span>
          </NavLink>
        )}

        {(me?.role === 'admin' || operator) && campaignDesk.data === true && (
          <NavLink to="/campaigns-desk"
                   className={({ isActive }) => `group relative flex items-center gap-3 pl-5 pr-3 py-2.5 transition-colors duration-150 ${isActive ? 'text-amber-glow' : 'text-paper-dim hover:text-paper'}`}>
            <span className="w-5 h-5 shrink-0"><IconBroadcast /></span>
            <span className="font-serif text-[14px] leading-none tracking-tight flex-1">Campaign Desk</span>
          </NavLink>
        )}

        {(me?.sections ?? []).length > 0 && (
          <NavLink to="/editorial"
                   className={({ isActive }) => `group relative flex items-center gap-3 pl-5 pr-3 py-2.5 mt-3 transition-colors duration-150 ${isActive ? 'text-amber-glow' : 'text-paper-dim hover:text-paper'}`}>
            <span className="w-5 h-5 shrink-0"><IconMegaphone /></span>
            <span className="font-serif text-[14px] leading-none tracking-tight flex-1">Editorial Desk</span>
            {tracksOpen > 0 && (
              <span className={`font-mono text-[10px] px-1.5 py-0.5 rounded tabular-nums ${tracksOverdue > 0 ? 'bg-amber-glow text-ink-900 font-semibold' : 'bg-amber-glow/20 text-amber-glow'}`}
                    title={tracksOverdue > 0 ? `${tracksOverdue} overdue` : undefined}>
                {tracksOpen > 999 ? '999+' : tracksOpen}{tracksOverdue > 0 ? ` · ${tracksOverdue} overdue` : ''}
              </span>
            )}
          </NavLink>
        )}

        {editor && editorDeskLinks(me).length > 0 && (
          <div className="mt-3" aria-label="Visitors">
            <div className="mb-1 px-5 label">Visitors</div>
            {editorDeskLinks(me).map((l) => (
              <NavLink key={l.to} to={l.to}
                       className={({ isActive }) => `block pl-5 pr-3 py-2 font-mono text-[12px] transition-colors duration-150 ${isActive ? 'text-amber-glow' : 'text-paper-dim hover:text-paper'}`}>
                {l.label}
              </NavLink>
            ))}
          </div>
        )}

        {!editor && lab?.enabled && (
          <>
            <div className="mt-5 mb-2 px-5 flex items-center gap-2">
              <span className="label">Lab</span>
              <span className="h-px flex-1 bg-ink-500" />
              <span className="font-mono text-[9px] tracking-label px-1.5 py-0.5 border border-amber-glow/40 text-amber-glow">BETA</span>
            </div>
            {LAB_NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) => `
                  group relative flex items-center gap-3 pl-5 pr-3 py-2.5
                  transition-colors duration-150
                  ${isActive ? 'text-amber-glow' : 'text-paper-dim hover:text-paper'}
                `}
              >
                {({ isActive }) => (
                  <>
                    <span className={`absolute left-0 top-0 bottom-0 w-[2px] ${isActive ? 'bg-amber-glow' : 'bg-transparent'}`} />
                    <span className={`w-5 h-5 shrink-0 ${isActive ? 'text-amber-glow' : 'text-paper-trace group-hover:text-paper-dim'}`}>
                      {item.icon}
                    </span>
                    <span className="font-mono text-[10px] tracking-label text-paper-trace">{item.code}</span>
                    <span className="font-serif text-[14px] leading-none tracking-tight flex-1">{item.label}</span>
                    {item.to === '/lab/issues' && stuckIssues > 0 && (
                      <span className="font-mono text-[10px] px-1.5 py-0.5 rounded bg-amber-glow/20 text-amber-glow tabular-nums">
                        {stuckIssues > 999 ? '999+' : stuckIssues}
                      </span>
                    )}
                  </>
                )}
              </NavLink>
            ))}
          </>
        )}
      </nav>

      <div className="px-5 py-4 border-t border-ink-500">
        <div className="label">Revision</div>
        <div className="font-mono text-[11px] text-paper-dim mt-1 tabular-nums">
          v0.1.0 · phase 1
        </div>
      </div>
    </aside>
  )
}

function SupportMenu({ summary, repliesOn, unthreaded }: { summary: DeskSummary; repliesOn: boolean; unthreaded: number }) {
  const loc = useLocation()
  const onSupport = loc.pathname === '/support' || loc.pathname.startsWith('/support/')
  const [userExpanded, setUserExpanded] = useState<boolean | null>(null)
  const expanded = userExpanded ?? onSupport
  const children = supportNavChildren(summary, { unthreaded }).filter((c) => c.to !== '/known-issues' || repliesOn)
  const total = badgeText(summary.total)
  return (
    <div className="mt-5">
      <div className="flex items-stretch">
        <NavLink
          to="/support"
          end
          className={({ isActive }) => `group relative flex flex-1 items-center gap-3 pl-5 pr-2 py-2.5 transition-colors duration-150 ${isActive ? 'text-amber-glow' : 'text-paper-dim hover:text-paper'}`}
        >
          {({ isActive }) => (
            <>
              <span className={`absolute left-0 top-0 bottom-0 w-[2px] ${isActive ? 'bg-amber-glow' : 'bg-transparent'}`} />
              <span className={`w-5 h-5 shrink-0 ${isActive ? 'text-amber-glow' : 'text-paper-trace group-hover:text-paper-dim'}`}>
                <IconMail />
              </span>
              <span className="font-serif text-[14px] leading-none tracking-tight flex-1">Customer Support</span>
              {total && (
                <span
                  className="font-mono text-[10px] px-1.5 py-0.5 rounded bg-amber-glow/20 text-amber-glow tabular-nums"
                >
                  {total}<span className="sr-only"> to review</span>
                </span>
              )}
            </>
          )}
        </NavLink>
        <button
          type="button"
          aria-expanded={expanded}
          aria-label="Customer Support sections"
          onClick={() => setUserExpanded(!expanded)}
          className="px-3 text-paper-trace hover:text-paper"
        >
          <span className={`inline-block transition-transform ${expanded ? 'rotate-90' : ''}`} aria-hidden="true">▸</span>
        </button>
      </div>
      {expanded && children.map((c) => (
        <NavLink
          key={c.to}
          to={c.to}
          end
          className={({ isActive }) => `group relative flex items-center pl-[60px] pr-3 py-1.5 transition-colors duration-150 ${isActive ? 'text-amber-glow' : 'text-paper-dim hover:text-paper'}`}
        >
          {({ isActive }) => (
            <>
              <span className={`absolute left-0 top-0 bottom-0 w-[2px] ${isActive ? 'bg-amber-glow' : 'bg-transparent'}`} />
              <span className="font-serif text-[12px] leading-none tracking-tight flex-1">{c.label}</span>
              {!!c.count && c.count > 0 && (
                <span
                  className="font-mono text-[10px] px-1.5 py-0.5 rounded bg-amber-glow/20 text-amber-glow tabular-nums"
                >
                  {badgeText(c.count)}<span className="sr-only"> {c.badgeLabel ?? 'to review'}</span>
                </span>
              )}
            </>
          )}
        </NavLink>
      ))}
    </div>
  )
}

function NavItem({
  item, pendingDupes, unreadContact = 0, unreadFeedback = 0, unreadEmail = 0, unthreaded = 0,
}: {
  item: Item
  pendingDupes: number
  unreadContact?: number
  unreadFeedback?: number
  unreadEmail?: number
  unthreaded?: number
}) {
  const loc = useLocation()
  const hasChildren = !!item.children?.length
  // Auto-expand when user is on a descendant route, so the active
  // sub-item is visible without an extra click. User can still
  // collapse manually via the chevron — local state takes over once
  // they interact.
  const isOnDescendantRoute = hasChildren && (
    loc.pathname === item.to ||
    loc.pathname.startsWith(item.to + '/')
  )
  const [userExpanded, setUserExpanded] = useState<boolean | null>(null)
  const expanded = userExpanded ?? isOnDescendantRoute

  return (
    <div>
      <NavLink
        to={item.to}
        end={item.to === '/' || hasChildren}
        // Click on a parent with children also toggles the children's
        // visibility — single-click affords both navigation and
        // expand/collapse without a separate hit target. The
        // navigation still happens via NavLink's default behavior.
        onClick={() => {
          if (hasChildren) setUserExpanded(!expanded)
        }}
        className={({ isActive }) => `
          group relative flex items-center gap-3 pl-5 pr-3 py-2.5
          transition-colors duration-150
          ${isActive ? 'text-amber-glow' : 'text-paper-dim hover:text-paper'}
        `}
      >
        {({ isActive }) => (
          <>
            <span
              className={`absolute left-0 top-0 bottom-0 w-[2px] ${isActive ? 'bg-amber-glow' : 'bg-transparent'}`}
            />
            <span className={`w-5 h-5 shrink-0 ${isActive ? 'text-amber-glow' : 'text-paper-trace group-hover:text-paper-dim'}`}>
              {item.icon}
            </span>
            <span className="font-mono text-[10px] tracking-label text-paper-trace">{item.code}</span>
            <span className="font-serif text-[14px] leading-none tracking-tight flex-1">{item.label}</span>
            {item.badge === 'contact' && unreadContact > 0 && (
              <span className="font-mono text-[10px] px-1.5 py-0.5 rounded bg-amber-glow/20 text-amber-glow tabular-nums">
                {unreadContact > 999 ? '999+' : unreadContact}
              </span>
            )}
            {item.badge === 'feedback' && unreadFeedback > 0 && (
              <span className="font-mono text-[10px] px-1.5 py-0.5 rounded bg-amber-glow/20 text-amber-glow tabular-nums">
                {unreadFeedback > 999 ? '999+' : unreadFeedback}
              </span>
            )}
            {item.badge === 'email' && unreadEmail > 0 && (
              <span className="font-mono text-[10px] px-1.5 py-0.5 rounded bg-amber-glow/20 text-amber-glow tabular-nums">
                {unreadEmail > 999 ? '999+' : unreadEmail}
              </span>
            )}
            {item.badge === 'unthreaded' && unthreaded > 0 && (
              <span className="font-mono text-[10px] px-1.5 py-0.5 rounded bg-amber-glow/20 text-amber-glow tabular-nums">
                {unthreaded > 999 ? '999+' : unthreaded}
              </span>
            )}
            {item.badge === 'duplicates' && pendingDupes > 0 && (
              <span className="font-mono text-[10px] px-1.5 py-0.5 rounded bg-amber-glow/20 text-amber-glow tabular-nums">
                {pendingDupes > 999 ? '999+' : pendingDupes}
              </span>
            )}
            {hasChildren && (
              <span className={`text-paper-trace inline-block transition-transform ${expanded ? 'rotate-90' : ''}`}>
                ▸
              </span>
            )}
          </>
        )}
      </NavLink>
      {hasChildren && expanded && item.children!.map((c) => (
        <NavLink
          key={c.to}
          to={c.to}
          end={c.end}
          className={({ isActive }) => `
            group relative flex items-center pl-[60px] pr-3 py-1.5
            transition-colors duration-150
            ${isActive || (c.match && loc.pathname.startsWith(c.match)) ? 'text-amber-glow' : 'text-paper-dim hover:text-paper'}
          `}
        >
          {({ isActive: linkActive }) => (
            <>
              <span
                className={`absolute left-0 top-0 bottom-0 w-[2px] ${linkActive || (c.match && loc.pathname.startsWith(c.match)) ? 'bg-amber-glow' : 'bg-transparent'}`}
              />
              <span className="font-serif text-[12px] leading-none tracking-tight">
                {c.label}
              </span>
            </>
          )}
        </NavLink>
      ))}
    </div>
  )
}

// Hand-drawn inline icons — keep them angular and schematic, not cutesy
function IconVisitors() {
  return (
    <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.3">
      <path d="M2 10 C5 5 15 5 18 10 C15 15 5 15 2 10 Z" />
      <circle cx="10" cy="10" r="2.5" />
    </svg>
  )
}

function IconGauge() {
  return (
    <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.3">
      <circle cx="10" cy="11" r="6" />
      <path d="M10 11 L13 7" strokeLinecap="round" />
      <path d="M4 11 L5.5 11 M14.5 11 L16 11 M10 5 L10 6.5" />
    </svg>
  )
}
function IconUsers() {
  return (
    <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.3">
      <circle cx="8" cy="7" r="2.5" />
      <path d="M3 16 C3 13 5 12 8 12 C11 12 13 13 13 16" />
      <circle cx="14" cy="8" r="1.8" />
      <path d="M17 15 C17 13 16 12 14 12" />
    </svg>
  )
}
function IconBranching() {
  return (
    <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.3">
      <circle cx="4" cy="10" r="1.5" />
      <circle cx="16" cy="4" r="1.5" />
      <circle cx="16" cy="16" r="1.5" />
      <path d="M5.5 10 L10 10 L14.5 4 M10 10 L14.5 16" />
    </svg>
  )
}
function IconOrbit() {
  return (
    <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.3">
      <circle cx="10" cy="10" r="6" />
      <ellipse cx="10" cy="10" rx="6" ry="2.5" transform="rotate(-30 10 10)" />
      <circle cx="10" cy="10" r="1.2" fill="currentColor" />
    </svg>
  )
}
function IconStream() {
  return (
    <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.3">
      <path d="M3 6 L17 6 M3 10 L13 10 M3 14 L17 14" strokeLinecap="round" />
      <circle cx="17" cy="10" r="1.2" fill="currentColor" />
    </svg>
  )
}
function IconEnvelope() {
  return (
    <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.3">
      <rect x="3" y="5" width="14" height="10" />
      <path d="M3 6 L10 11 L17 6" strokeLinejoin="round" />
    </svg>
  )
}
function IconMail() {
  // Postal envelope with a stamp corner — distinct from the email envelope.
  return (
    <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.3">
      <rect x="3" y="5" width="14" height="10" />
      <path d="M3 6 L10 11 L17 6" strokeLinejoin="round" />
      <rect x="13" y="5.5" width="3.5" height="3" fill="currentColor" stroke="none" />
    </svg>
  )
}
function IconBroadcast() {
  return (
    <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.3">
      <circle cx="10" cy="10" r="1.5" fill="currentColor" />
      <path d="M7 7 C5.5 8.5 5.5 11.5 7 13 M13 7 C14.5 8.5 14.5 11.5 13 13" strokeLinecap="round" />
      <path d="M5 5 C2.5 7.5 2.5 12.5 5 15 M15 5 C17.5 7.5 17.5 12.5 15 15" strokeLinecap="round" />
    </svg>
  )
}
function IconLabTemplate() {
  // Envelope (template body) with a small overlay tile (the dynamic-content slot).
  return (
    <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.3">
      <rect x="2" y="5" width="13" height="9" />
      <path d="M2 6 L8.5 11 L15 6" strokeLinejoin="round" />
      <rect x="11" y="11" width="6" height="5" fill="currentColor" stroke="none" />
    </svg>
  )
}

function IconDryRun() {
  // Two stacked rectangles + a play-triangle — "preview a run".
  return (
    <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.3">
      <rect x="3" y="4" width="11" height="3" />
      <rect x="3" y="9" width="11" height="3" />
      <path d="M14 14 L18 16 L14 18 Z" strokeLinejoin="round" fill="currentColor" />
    </svg>
  )
}

function IconFilm() {
  // Filmstrip with sprocket holes — "video / trailer".
  return (
    <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.3">
      <rect x="3" y="4" width="14" height="12" />
      <rect x="5" y="6" width="2" height="2" fill="currentColor" stroke="none" />
      <rect x="13" y="6" width="2" height="2" fill="currentColor" stroke="none" />
      <rect x="5" y="12" width="2" height="2" fill="currentColor" stroke="none" />
      <rect x="13" y="12" width="2" height="2" fill="currentColor" stroke="none" />
      <path d="M9 8 L13 10 L9 12 Z" strokeLinejoin="round" fill="currentColor" />
    </svg>
  )
}

function IconMegaphone() {
  return (
    <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.3">
      <path d="M3 8 L13 4 L13 16 L3 12 Z" strokeLinejoin="round" />
      <path d="M13 8 C16 8 16 12 13 12" />
      <path d="M5 13 L5 16 L8 16 L7 13" strokeLinejoin="round" />
    </svg>
  )
}

function IconSelector() {
  // Funnel — wide top, narrow bottom, with a dot below for "selected output".
  return (
    <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.3">
      <path d="M3 4 L17 4 L12 11 L12 15 L8 17 L8 11 Z" strokeLinejoin="round" />
    </svg>
  )
}

function IconArchive() {
  // Library shelf — stacked documents with a horizontal bar beneath (the shelf).
  return (
    <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.3">
      <rect x="4" y="3" width="3" height="11" strokeLinejoin="round" />
      <rect x="8" y="5" width="3" height="9" strokeLinejoin="round" />
      <rect x="12" y="4" width="3" height="10" strokeLinejoin="round" />
      <path d="M2.5 15 L17.5 15" strokeLinecap="round" />
    </svg>
  )
}
function IconDuplicates() {
  // Two overlapping document-shapes with a bridging arrow — "merge candidate".
  return (
    <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.3">
      <rect x="3" y="4" width="7" height="9" strokeLinejoin="round" />
      <rect x="10" y="7" width="7" height="9" strokeLinejoin="round" />
      <path d="M7 9 L11 9" strokeLinecap="round" />
      <path d="M9 7.5 L11 9 L9 10.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}
function IconReceipt() {
  return (
    <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.3">
      <path d="M5 3 L15 3 L15 17 L12.5 15.5 L10 17 L7.5 15.5 L5 17 Z" />
      <path d="M7.5 7 L12.5 7 M7.5 10 L12.5 10" strokeLinecap="round" />
    </svg>
  )
}
function IconAuditCheck() {
  // Clipboard with a checkmark + small bar chart — "report card".
  return (
    <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.3">
      <rect x="4" y="3" width="12" height="14" strokeLinejoin="round" />
      <path d="M7 3 L7 5 L13 5 L13 3" strokeLinejoin="round" />
      <path d="M7 9 L9 11 L13 7" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M7 13 L13 13" strokeLinecap="round" />
    </svg>
  )
}
